import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../lib/axios";
import { BACKEND_URL } from "../../config";

const defaultStats = {
  totalConsultancies: 0,
  totalStudents: 0,
  totalOffers: 0,
  totalLeadsToday: 0,
  activeSubs: 0,
  consultancyDatabase: {
    totalDiscovered: 0,
    totalUsable: 0,
    totalVerified: 0,
    totalClaimed: 0,
    totalPublished: 0,
    totalRejected: 0,
    totalDuplicates: 0,
    totalPendingReview: 0,
    target: 1000,
    remaining: 1000,
    percentage: 0,
    verifiedThisWeek: 0,
    verifiedThisMonth: 0,
    newDiscoveries: 0,
  },
};

const statMeta = [
  { key: "totalConsultancies", label: "Consultancies", icon: "🏢", gradient: "from-indigo-500 to-purple-500" },
  { key: "totalStudents", label: "Students", icon: "🎓", gradient: "from-blue-500 to-cyan-500" },
  { key: "totalOffers", label: "Offers", icon: "🎁", gradient: "from-pink-500 to-rose-500" },
  { key: "totalLeadsToday", label: "Leads Today", icon: "⚡", gradient: "from-emerald-500 to-lime-500" },
  { key: "activeSubs", label: "Active Subs", icon: "⭐", gradient: "from-amber-400 to-orange-500" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(defaultStats);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState("");
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState("");
  const [quickActionLoading, setQuickActionLoading] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    setStatsError("");
    try {
      console.log("Admin dashboard token:", token);
      const res = await axios.get(`${BACKEND_URL}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const consultancyStats = await axios.get(`${BACKEND_URL}/admin/consultancies/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats({
        ...defaultStats,
        ...res.data,
        consultancyDatabase: {
          ...defaultStats.consultancyDatabase,
          ...consultancyStats.data,
        },
      });
    } catch (err) {
      console.error("Failed to load dashboard stats", err);
      setStatsError("Error loading stats");
    } finally {
      setLoadingStats(false);
    }
  }, [token]);

  const fetchActivities = useCallback(async () => {
    setActivityLoading(true);
    setActivityError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/admin/activities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivities(res.data || []);
    } catch (err) {
      console.error("Failed to load activities", err);
      setActivityError("Unable to load recent activities");
    } finally {
      setActivityLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    fetchActivities();
  }, [fetchStats, fetchActivities]);

  const approveAllConsultancies = async () => {
    setQuickActionLoading(true);
    try {
      if (!token) {
        alert("Token missing - please login again");
        setQuickActionLoading(false);
        return;
      }
      console.log("Calling API...");
      const res = await axios.put(
        `${BACKEND_URL}/admin/consultancies/approve-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("RESPONSE:", res.data);
      alert(res.data.message || "Approved successfully!");
      fetchStats();
      fetchActivities();
    } catch (error) {
      console.error("❌ FRONTEND ERROR:", error.response?.data || error.message);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Server Error - Check console"
      );
    } finally {
      setQuickActionLoading(false);
    }
  };

  const getFlaggedStudents = async () => {
    setQuickActionLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/admin/students/flagged`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("FLAGGED:", res.data);
      alert(
        res.data.length ? `${res.data.length} flagged students found` : "No flagged students currently."
      );
      navigate("/admin/students?filter=flagged");
    } catch (error) {
      console.error("FLAGGED ERROR:", error.response?.data || error.message);
      alert(error.response?.data?.message || "Error loading flagged students");
    } finally {
      setQuickActionLoading(false);
    }
  };

  const sendPlatformUpdate = async () => {
    setQuickActionLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/admin/notify-all`,
        {
          subject: "EduOyo Platform Update",
          message: "New features have been released on the platform.",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("NOTIFY RESPONSE:", res.data);
      alert("Email sent to all users!");
    } catch (error) {
      console.error("NOTIFY ERROR:", error.response?.data || error.message);
      alert(error.response?.data?.message || "Error sending mail");
    } finally {
      setQuickActionLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow-[0_30px_80px_rgba(15,23,42,0.4)]">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[6px] text-white/60">EduOyo Admin Control Center</p>
            <h2 className="text-3xl font-semibold">Operational Overview</h2>
            <p className="text-white/60 mt-1">
              Monitor platform activity, approvals, and growth metrics in real time.
            </p>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-sm">Status</p>
            <p className="text-lg font-semibold text-emerald-300">Systems Stable ✅</p>
          </div>
        </div>

        {statsError && (
          <div className="mt-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {statsError}
          </div>
        )}

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {statMeta.map((meta) => (
            <div
              key={meta.key}
              className={`rounded-2xl bg-gradient-to-br ${meta.gradient} p-4 shadow-lg hover:-translate-y-1 transition`}
            >
              <div className="text-2xl">{meta.icon}</div>
              <p className="text-3xl font-bold mt-2">
                {loadingStats ? (
                  <span className="inline-block h-6 w-16 rounded bg-white/40 animate-pulse" />
                ) : (
                  stats[meta.key]?.toLocaleString()
                )}
              </p>
              <p className="text-xs uppercase tracking-[3px] text-white/70">{meta.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[5px] text-white/60">
              AbroadJacks Consultancy Target
            </p>
            <h3 className="mt-2 text-2xl font-semibold">
              {stats.consultancyDatabase.totalVerified.toLocaleString()} /{" "}
              {stats.consultancyDatabase.target.toLocaleString()} verified
            </h3>
            <p className="mt-1 text-sm text-white/60">
              {stats.consultancyDatabase.remaining.toLocaleString()} remaining for the first milestone.
            </p>
          </div>
          <div className="w-full lg:max-w-md">
            <div className="h-3 rounded-full bg-white/10">
              <div
                className="h-3 rounded-full bg-emerald-400"
                style={{ width: `${Math.min(stats.consultancyDatabase.percentage, 100)}%` }}
              />
            </div>
            <div className="mt-2 text-right text-sm text-white/70">
              {stats.consultancyDatabase.percentage}%
            </div>
          </div>
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          {[
            ["Discovered", stats.consultancyDatabase.totalDiscovered],
            ["Usable", stats.consultancyDatabase.totalUsable],
            ["Claimed", stats.consultancyDatabase.totalClaimed],
            ["Published", stats.consultancyDatabase.totalPublished],
            ["Pending Review", stats.consultancyDatabase.totalPendingReview],
            ["Duplicates", stats.consultancyDatabase.totalDuplicates],
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <p className="text-2xl font-semibold">{Number(value).toLocaleString()}</p>
              <p className="text-xs uppercase tracking-[3px] text-white/50">{label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Recent Activity</h3>
            <button
              type="button"
              onClick={fetchActivities}
              className="text-sm text-blue-300 hover:text-blue-100"
            >
              Refresh
            </button>
          </div>
          {activityLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 animate-pulse"
                >
                  <div className="h-4 bg-white/20 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-white/10 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : activityError ? (
            <div className="rounded-2xl border border-white/10 bg-rose-500/10 px-4 py-6 text-center text-rose-200">
              {activityError}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center text-white/60 py-8">No recent activity found.</div>
          ) : (
            <ul className="space-y-4">
              {activities.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <div>
                    <p className="font-semibold">{item.title}</p>
                    <p className="text-white/60 text-sm">{item.description}</p>
                  </div>
                  <span className="text-white/50 text-xs">{item.time}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6">
          <h3 className="text-xl font-semibold mb-4">Quick Actions</h3>
          <div className="grid gap-3">
            <button
              onClick={approveAllConsultancies}
              className="w-full text-left px-4 py-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition disabled:opacity-40"
              disabled={quickActionLoading}
            >
              Approve pending consultancies
            </button>
            <button
              onClick={getFlaggedStudents}
              className="w-full text-left px-4 py-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition disabled:opacity-40"
              disabled={quickActionLoading}
            >
              Review flagged students
            </button>
            <button
              onClick={sendPlatformUpdate}
              className="w-full text-left px-4 py-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition disabled:opacity-40"
              disabled={quickActionLoading}
            >
              Send platform update email
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
